import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ErrorNotify from "../../components/ErrorNotify/ErrorNotify";
import { Form } from "../../components/Form/Form";
import MainModal from "../../components/Modal/Modal";
import { Tabs } from "../../components/Tabs";
import type { IRootState } from "../../redux/store";
import {
	BrainData,
	Scene,
	VoiceTypes,
	fetchBrainData,
	filterVoicesByGender,
	listPlatformAvatars,
	listPlatformScenes,
	tabsList,
	useVisperSearchParams,
} from "../../utils";
import style from "./Avatar.module.scss";
import { useDispatch, useSelector } from "react-redux";
import PreviewAvatarSection from "../../components/PreviewAvatarSection/PreviewAvatarSection";
import Spinner from "../../components/Spinner/Spinner";
import PreviewAvatarMedia from "../../components/PreviewAvatarMedia/PreviewAvatarMedia";
import { changeFormData, resetFormData, setAvatar, setBrainId, setPlatformAvatars, setPlatformScenes } from "../../redux/slices";
import { useTabs } from "../../components/Tabs/TabsProvider";
import { LINKS } from "../../const";
import { avatarsGender, CUSTOM_VOICES, defaultAvatar, neuralAvatars, scenesId } from "./constants";
import logo from "../../assets/pictures/visper-logo-scene.svg";
import { setAppSessionStart } from "../../utils/cookieUtils";
import { enrichAvatarsWithGender, formatCharactersToAvatars } from "../../utils/shared";
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from "react-i18next";
import { ReturnButton } from "../../components/ReturnButton/ReturnButton";
import ContactModal from "../../components/ContactModal/ContactModal";

const DEFAULT_GENDER = "female";

const AvatarPage = () => {
	const { activeTab, setActiveTab } = useTabs();
	const [modal, setModal] = useState<"error" | "none" | "contact">("none");
	const { i18n } = useTranslation();
	const { t } = useTranslation('modals');
	const { t: tForm } = useTranslation('form');
	const memoizedTabsList = useMemo(() => tabsList, []);
	const formData = useSelector((state: IRootState) => state.avatarForm);
	const avatar = useSelector((state: IRootState) => state.avatar);
	const isPreviewSection = activeTab === "preview";
	const [error, setError] = useState<"general" | "brainId" | "brainValidation" | "">("");
	const [isError, setIsError] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const brainIdErrorMessage = t("avatar_prompt_save_error");
	const brainValidationErrorMessage = t("prompt_policy_violation");
	const brainValidationSubErrorMessage = t("prompt_support_message");
	const brainValidationError = "CensureError: Промпт не прошел цензуру";
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [searchParams, setSearchParams] = useSearchParams();
	const source = searchParams.get("source");
	const lang = searchParams.get("lang");
	const user = searchParams.get("user");
	const userSub = searchParams.get("sub");
	const dispatch = useDispatch();
	useVisperSearchParams(lang, source, user, userSub, searchParams, setSearchParams);

	const fetchPlatformScenes = useCallback(() => {
			return listPlatformScenes()
				.then((res) => res.json())
				.then((json) => {
					const scenes = json
					.filter((el: Scene) => scenesId.includes(el.id))
					.map((el: Scene) => ({
						value: el.id,
						description: el.name,
					}));
					dispatch(setPlatformScenes(scenes));
				});
	}, []);
	
	const fetchPlatformAvatars = useCallback(() => {
		return listPlatformAvatars()
				.then((res) => res.json())
				.then((json) => {
					const formatted = formatCharactersToAvatars(json);
					const avatars = enrichAvatarsWithGender(formatted, avatarsGender, DEFAULT_GENDER);
					const allAvatars = [...neuralAvatars, ...avatars];
					dispatch(setPlatformAvatars(allAvatars));
			});
	}, [neuralAvatars]);
	
	const fetchAllData = useCallback(() => {
			setIsError(false);
			Promise.all([fetchPlatformScenes(), fetchPlatformAvatars()])
				.then(([_scenes, _avatars]) => {
					const avatarId = uuidv4();
					const randomAvatar = neuralAvatars.find((avatar) => avatar.value === defaultAvatar) || neuralAvatars[0];
					const { gender, avatarRuName, value, promptKey, description } = randomAvatar;
					const genderPropmt = gender === "male" ? "male_prompt" : "female_prompt";
					const genderVoices: VoiceTypes[] = filterVoicesByGender(CUSTOM_VOICES, gender);
					const defaultText = tForm(promptKey || genderPropmt, { name: tForm(description) });
					dispatch(setAvatar({ name: avatarRuName, gender, brainId: "", avatarId, promptKey: promptKey || genderPropmt, enName: description, type: randomAvatar.type || "3d" }));
					dispatch(changeFormData({ type: value, voice: genderVoices[0].voice_id, prompt: defaultText, ttsEngine: genderVoices[0].ttsEngine }));
				})
				.catch(() => {
					setIsError(true);
					setError("general");
				})
				.finally(() => {
					setIsLoading(false);
				});
	}, [fetchPlatformScenes, fetchPlatformAvatars]);
	
	useEffect(() => {
		setIsLoading(true);
		dispatch(resetFormData());
		dispatch(changeFormData({ logo }));
		fetchAllData();
		setActiveTab("appearance");
		setAppSessionStart();
	}, []);

	const fetchBrainId = useCallback((prompt?: string) => {
        setIsError(false);
        setIsLoading(true);
		const promptForFetch = prompt || formData.prompt;
        return fetchBrainData(promptForFetch)
            .then((res) => res.json())
            .then((json: BrainData) => {
				if (!json.brain_id) {
					setError("brainId");
					setIsError(true);
					setErrorMessage(brainIdErrorMessage);
				}
                dispatch(setBrainId(json.brain_id));
            })
            .catch((error) => {
				setIsError(true);
				dispatch(setBrainId(""));
				const message = (error && error.error && error.error.message) ? error.error.message : "";
				if (message === brainValidationError) {
					setError("brainValidation");
					setErrorMessage(brainValidationErrorMessage);
					return;
				}
				setError("brainId");
				setErrorMessage(brainIdErrorMessage);
			})
            .finally(() => setIsLoading(false));
    }, [formData.prompt]);

	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		});
	}, [activeTab]);

	useEffect(() => {
		if (isError) {
			setModal("error");
		}
	}, [isError]);

	useEffect(() => {
		if (error && (error === "brainValidation" || error === "brainId")) {
			setActiveTab("appearance");
		}
	}, [error]);

	useEffect(() => {
		if (activeTab === "preview" && !avatar.brainId) {
			fetchBrainId();
		}
	}, [activeTab, avatar.brainId, fetchBrainId]);

	const handleClosePromptError = useCallback(() => {
		setError("general");
		setIsError(false);
		setErrorMessage("");
		setModal("none");
		setActiveTab("appearance");
	}, []);

	const handleCloseModal = useCallback(() => {
		setModal("none");
	}, []);

	const avatarFullScreen =
		activeTab !== null && ["preview"].includes(activeTab);

	const renderModal = () => {
		if (modal === "none") return null;
		const isPromptError = error === "brainValidation" || error === "brainId";
		const isValidationPromptError = error === "brainValidation";
		const errorButtonText = isValidationPromptError ? t("contact_support") : "";

		const modalComponents = {
			error:
				<ErrorNotify
					onClose={isPromptError ? handleClosePromptError : handleCloseModal} 
					errorMessage={errorMessage}
					primaryButtonText={errorButtonText}
					subErrorText={isValidationPromptError ? brainValidationSubErrorMessage : ""}
					primaryButtonAction={isValidationPromptError ? () => window.open(LINKS.telegram, "_blank") : undefined}
				/>,
			contact: <ContactModal onClose={handleCloseModal} avatarType={avatar.type} />,
		};

		return <MainModal>{modalComponents[modal]}</MainModal>;
	};

	if (isLoading) {
		return (
			<div className={style.spinnerWrapper}>
				<Spinner size={"medium"} />
			</div>
		);
	}

	return (
		<div className={style.pageContainer}>
			{modal !== "none" && renderModal()}
			<div className={style.navContainer}>
				<ReturnButton />
				<Tabs
					tabsList={memoizedTabsList}
					openPublishButton={() => setModal("contact")}
				/>
			</div>
			<div className={style.container}>
				<div className={style.formWrapper}>
					{!avatarFullScreen && activeTab && (
						<Form getBrainId={fetchBrainId} />
					)}
				</div>
				{!isPreviewSection && (
					<PreviewAvatarMedia />
				)}
				{isPreviewSection && (
					<PreviewAvatarSection />
				)}
			</div>
		</div>
	);
};

export default AvatarPage;
